// Code generated by @hex-insights/frontend-generator; While this comment is present this file may be overwritten. If manual edits are made, remove this comment.
import React from "react";
import { ApolloCache, FetchResult, gql } from "@apollo/client";
import { subsetTypeHasKey } from "@hex-insights/core";
import {
	Address,
	Country,
	EmailAddress,
	Employee,
	graphTypeNames,
	Guardian,
	Language,
	Person,
	PersonCreateInput,
	PersonCreateMutation,
	PersonDeleteMutation,
	PersonDetailDocument,
	PersonDetailQuery,
	PersonDetailQueryVariables,
	PersonUpdateInput,
	PersonUpdateMutation,
	PhoneNumber,
	Student,
	usePersonCreateMutation,
	usePersonDeleteMutation,
	usePersonUpdateMutation,
	User,
} from "../../../Utilities";
import * as PersonFormConversion from "./conversion";
import * as PersonFormValues from "./form-values";
import { customUpdateCacheForCreate, customUpdateCacheForDelete, customUpdateCacheForUpdate } from "./mutation-custom";

/**
 * Returns a `create` function for the Person model. The `create` function translates the given
 * `formValues` to the GraphQL create input for the Person model.
 */
export function useCreate<K extends keyof PersonFormValues.Create = keyof PersonFormValues.Create>() {
	const create = useDirectCreate();

	return React.useCallback(
		async (formValues: PersonFormValues.CreateSubset<K>) => {
			const input = PersonFormConversion.toGQLCreateInput(formValues);
			return create(input);
		},
		[create],
	);
}

/**
 * Returns a `create` function for the Person model. The `create` function accepts a GraphQL
 * create input for the model.
 */
export function useDirectCreate() {
	const [createPerson] = usePersonCreateMutation();

	return React.useCallback(
		async (input: PersonCreateInput) => {
			const updateCache = getUpdateCacheForCreate(input);

			const { data, errors } = await createPerson({ variables: { input }, update: updateCache });

			return { data: data?.createPerson ?? null, errors: errors ?? null };
		},
		[createPerson],
	);
}

/**
 * Returns an `update` function for the Person model. The `update` function translates the given
 * `formValues` to the GraphQL update input for the Person model.
 *
 * @param id The ID of the instance to update.
 */
export function useUpdate<K extends keyof PersonFormValues.Detail = keyof PersonFormValues.Detail>(id: Person["id"]) {
	const update = useDirectUpdate<K>(id);

	return React.useCallback(
		async (
			changedFormValues: Partial<PersonFormValues.DetailSubset<K>>,
			initialFormValues: PersonFormValues.DetailSubset<K>,
		) => {
			const input = PersonFormConversion.toGQLUpdateInput(changedFormValues, initialFormValues);
			return update(input, initialFormValues);
		},
		[update],
	);
}

/**
 * Returns an `update` function for the Person model. The `update` function accepts a GraphQL
 * update input for the model. The `initialFormValues` argument of the returned function should include at least those
 * keys that will be updated by the mutation.
 *
 * @param id The ID of the instance to update.
 */
export function useDirectUpdate<K extends keyof PersonFormValues.Detail = keyof PersonFormValues.Detail>(
	id: Person["id"],
) {
	const [updatePerson] = usePersonUpdateMutation();

	return React.useCallback(
		async (input: PersonUpdateInput, initialFormValues: PersonFormValues.DetailSubset<K>) => {
			const updateCache = getUpdateCacheForUpdate(input, initialFormValues);

			const { data, errors } = await updatePerson({ variables: { id, input }, update: updateCache });

			return { data: data?.updatePerson ?? null, errors: errors ?? null };
		},
		[updatePerson, id],
	);
}

/**
 * Returns a `del` function for the Person model.
 *
 * @param id The ID of the instance to delete.
 */
export function useDelete(id: Person["id"]) {
	const [deletePerson] = usePersonDeleteMutation();

	return React.useCallback(async () => {
		const updateCache = getUpdateCacheForDelete(id);

		const { data, errors } = await deletePerson({ variables: { id }, update: updateCache });

		return { data: data?.deletePerson ?? false, errors: errors ?? null };
	}, [deletePerson, id]);
}

function getUpdateCacheForCreate(input: PersonCreateInput) {
	return (
		cache: ApolloCache<any>,
		result: Omit<FetchResult<PersonCreateMutation, Record<string, any>, Record<string, any>>, "context">,
	) => {
		const createdObject = result.data?.createPerson;
		if (!createdObject) {
			return;
		}

		cache.writeQuery<PersonDetailQuery, PersonDetailQueryVariables>({
			query: PersonDetailDocument,
			data: { person: createdObject },
			variables: { id: createdObject.id },
		});

		if (input.addressIDs) {
			for (let i = 0; i < input.addressIDs.length; i++) {
				addToPeopleOfAddressCache(cache, input.addressIDs[i], createdObject);
			}
		}

		if (input.emailAddressIDs) {
			for (let i = 0; i < input.emailAddressIDs.length; i++) {
				addToPersonOfEmailAddressCache(cache, input.emailAddressIDs[i], createdObject);
			}
		}

		if (input.employeeID) {
			addToPersonOfEmployeeCache(cache, input.employeeID, createdObject);
		}

		if (input.guardianID) {
			addToPersonOfGuardianCache(cache, input.guardianID, createdObject);
		}

		if (input.languageIDs) {
			for (let i = 0; i < input.languageIDs.length; i++) {
				addToPeopleOfLanguageCache(cache, input.languageIDs[i], createdObject);
			}
		}

		if (input.nationalityIDs) {
			for (let i = 0; i < input.nationalityIDs.length; i++) {
				addToPeopleOfCountryCache(cache, input.nationalityIDs[i], createdObject);
			}
		}

		if (input.phoneNumberIDs) {
			for (let i = 0; i < input.phoneNumberIDs.length; i++) {
				addToPersonOfPhoneNumberCache(cache, input.phoneNumberIDs[i], createdObject);
			}
		}

		if (input.studentID) {
			addToPersonOfStudentCache(cache, input.studentID, createdObject);
		}

		if (input.userID) {
			addToPersonOfUserCache(cache, input.userID, createdObject);
		}

		cache.evict({ id: "ROOT_QUERY", fieldName: "personConnection" });

		if (input.emailAddresses && input.emailAddresses.length > 0) {
			cache.evict({ id: "ROOT_QUERY", fieldName: "emailAddressConnection" });
		}

		if (input.employee) {
			cache.evict({ id: "ROOT_QUERY", fieldName: "employeeConnection" });
		}

		if (input.guardian) {
			cache.evict({ id: "ROOT_QUERY", fieldName: "guardianConnection" });
		}

		if (input.phoneNumbers && input.phoneNumbers.length > 0) {
			cache.evict({ id: "ROOT_QUERY", fieldName: "phoneNumberConnection" });
		}

		if (input.student) {
			cache.evict({ id: "ROOT_QUERY", fieldName: "studentConnection" });
		}

		if (input.user) {
			cache.evict({ id: "ROOT_QUERY", fieldName: "userConnection" });
		}

		customUpdateCacheForCreate(input, cache, result);
	};
}

function getUpdateCacheForUpdate<K extends keyof PersonFormValues.Detail = keyof PersonFormValues.Detail>(
	input: PersonUpdateInput,
	initialFormValues: PersonFormValues.DetailSubset<K>,
) {
	return (
		cache: ApolloCache<any>,
		result: Omit<FetchResult<PersonUpdateMutation, Record<string, any>, Record<string, any>>, "context">,
	) => {
		const updatedObject = result.data?.updatePerson;
		if (!updatedObject) {
			return;
		}

		if (input.addAddressIDs) {
			for (let i = 0; i < input.addAddressIDs.length; i++) {
				addToPeopleOfAddressCache(cache, input.addAddressIDs[i], updatedObject);
			}
		}
		if (input.removeAddressIDs) {
			for (let i = 0; i < input.removeAddressIDs.length; i++) {
				removeFromPeopleOfAddressCache(cache, input.removeAddressIDs[i], updatedObject);
			}
		}

		if (input.addEmailAddressIDs) {
			for (let i = 0; i < input.addEmailAddressIDs.length; i++) {
				addToPersonOfEmailAddressCache(cache, input.addEmailAddressIDs[i], updatedObject);
			}
		}
		if (input.removeEmailAddressIDs) {
			for (let i = 0; i < input.removeEmailAddressIDs.length; i++) {
				removeFromPersonOfEmailAddressCache(cache, input.removeEmailAddressIDs[i], updatedObject);
			}
		}

		if (
			subsetTypeHasKey(initialFormValues, "employeeID") &&
			initialFormValues.employeeID &&
			(input.employeeID || input.clearEmployee)
		) {
			removeFromPersonOfEmployeeCache(cache, initialFormValues.employeeID, updatedObject);
		}
		if (input.employeeID) {
			addToPersonOfEmployeeCache(cache, input.employeeID, updatedObject);
		}

		if (
			subsetTypeHasKey(initialFormValues, "guardianID") &&
			initialFormValues.guardianID &&
			(input.guardianID || input.clearGuardian)
		) {
			removeFromPersonOfGuardianCache(cache, initialFormValues.guardianID, updatedObject);
		}
		if (input.guardianID) {
			addToPersonOfGuardianCache(cache, input.guardianID, updatedObject);
		}

		if (input.addLanguageIDs) {
			for (let i = 0; i < input.addLanguageIDs.length; i++) {
				addToPeopleOfLanguageCache(cache, input.addLanguageIDs[i], updatedObject);
			}
		}
		if (input.removeLanguageIDs) {
			for (let i = 0; i < input.removeLanguageIDs.length; i++) {
				removeFromPeopleOfLanguageCache(cache, input.removeLanguageIDs[i], updatedObject);
			}
		}

		if (input.addNationalityIDs) {
			for (let i = 0; i < input.addNationalityIDs.length; i++) {
				addToPeopleOfCountryCache(cache, input.addNationalityIDs[i], updatedObject);
			}
		}
		if (input.removeNationalityIDs) {
			for (let i = 0; i < input.removeNationalityIDs.length; i++) {
				removeFromPeopleOfCountryCache(cache, input.removeNationalityIDs[i], updatedObject);
			}
		}

		if (input.addPhoneNumberIDs) {
			for (let i = 0; i < input.addPhoneNumberIDs.length; i++) {
				addToPersonOfPhoneNumberCache(cache, input.addPhoneNumberIDs[i], updatedObject);
			}
		}
		if (input.removePhoneNumberIDs) {
			for (let i = 0; i < input.removePhoneNumberIDs.length; i++) {
				removeFromPersonOfPhoneNumberCache(cache, input.removePhoneNumberIDs[i], updatedObject);
			}
		}

		if (
			subsetTypeHasKey(initialFormValues, "studentID") &&
			initialFormValues.studentID &&
			(input.studentID || input.clearStudent)
		) {
			removeFromPersonOfStudentCache(cache, initialFormValues.studentID, updatedObject);
		}
		if (input.studentID) {
			addToPersonOfStudentCache(cache, input.studentID, updatedObject);
		}

		if (
			subsetTypeHasKey(initialFormValues, "userID") &&
			initialFormValues.userID &&
			(input.userID || input.clearUser)
		) {
			removeFromPersonOfUserCache(cache, initialFormValues.userID, updatedObject);
		}
		if (input.userID) {
			addToPersonOfUserCache(cache, input.userID, updatedObject);
		}

		customUpdateCacheForUpdate(input, initialFormValues, cache, result);
	};
}

function getUpdateCacheForDelete(id: Person["id"]) {
	return (
		cache: ApolloCache<any>,
		result: Omit<FetchResult<PersonDeleteMutation, Record<string, any>, Record<string, any>>, "context">,
	) => {
		if (!result.data?.deletePerson) {
			return;
		}

		cache.evict({ id: cache.identify({ id, __typename: graphTypeNames.Person }) });
		cache.evict({ id: "ROOT_QUERY", fieldName: "personConnection" });
		cache.gc();

		customUpdateCacheForDelete(id, cache, result);
	};
}

function addToPeopleOfAddressCache(cache: ApolloCache<any>, targetID: Address["id"], object: Pick<Person, "id">) {
	const objectRef = toCacheRef(cache, object);
	cache.modify({
		id: cache.identify({ id: targetID, __typename: graphTypeNames.Address }),
		fields: {
			people: (cachedValue) => [...cachedValue, objectRef],
		},
	});
}

function removeFromPeopleOfAddressCache(cache: ApolloCache<any>, targetID: Address["id"], object: Pick<Person, "id">) {
	const cacheID = cache.identify(object);
	cache.modify({
		id: cache.identify({ id: targetID, __typename: graphTypeNames.Address }),
		fields: {
			people: (cachedValue) => cachedValue.filter((e: any) => e.__ref !== cacheID),
		},
	});
}

function addToPersonOfEmailAddressCache(
	cache: ApolloCache<any>,
	targetID: EmailAddress["id"],
	object: Pick<Person, "id">,
) {
	const objectRef = toCacheRef(cache, object);
	cache.modify({
		id: cache.identify({ id: targetID, __typename: graphTypeNames.EmailAddress }),
		fields: {
			person: () => objectRef,
			personID: () => object.id,
		},
	});
}

function removeFromPersonOfEmailAddressCache(
	cache: ApolloCache<any>,
	targetID: EmailAddress["id"],
	_object: Pick<Person, "id">,
) {
	cache.modify({
		id: cache.identify({ id: targetID, __typename: graphTypeNames.EmailAddress }),
		fields: {
			person: () => null,
			personID: () => null,
		},
	});
}

function addToPersonOfEmployeeCache(cache: ApolloCache<any>, targetID: Employee["id"], object: Pick<Person, "id">) {
	const objectRef = toCacheRef(cache, object);
	cache.modify({
		id: cache.identify({ id: targetID, __typename: graphTypeNames.Employee }),
		fields: {
			person: () => objectRef,
			personID: () => object.id,
		},
	});
}

function removeFromPersonOfEmployeeCache(
	cache: ApolloCache<any>,
	targetID: Employee["id"],
	_object: Pick<Person, "id">,
) {
	cache.modify({
		id: cache.identify({ id: targetID, __typename: graphTypeNames.Employee }),
		fields: {
			person: () => null,
			personID: () => null,
		},
	});
}

function addToPersonOfGuardianCache(cache: ApolloCache<any>, targetID: Guardian["id"], object: Pick<Person, "id">) {
	const objectRef = toCacheRef(cache, object);
	cache.modify({
		id: cache.identify({ id: targetID, __typename: graphTypeNames.Guardian }),
		fields: {
			person: () => objectRef,
			personID: () => object.id,
		},
	});
}

function removeFromPersonOfGuardianCache(
	cache: ApolloCache<any>,
	targetID: Guardian["id"],
	_object: Pick<Person, "id">,
) {
	cache.modify({
		id: cache.identify({ id: targetID, __typename: graphTypeNames.Guardian }),
		fields: {
			person: () => null,
			personID: () => null,
		},
	});
}

function addToPeopleOfLanguageCache(cache: ApolloCache<any>, targetID: Language["id"], object: Pick<Person, "id">) {
	const objectRef = toCacheRef(cache, object);
	cache.modify({
		id: cache.identify({ id: targetID, __typename: graphTypeNames.Language }),
		fields: {
			people: (cachedValue) => [...cachedValue, objectRef],
		},
	});
}

function removeFromPeopleOfLanguageCache(
	cache: ApolloCache<any>,
	targetID: Language["id"],
	object: Pick<Person, "id">,
) {
	const cacheID = cache.identify(object);
	cache.modify({
		id: cache.identify({ id: targetID, __typename: graphTypeNames.Language }),
		fields: {
			people: (cachedValue) => cachedValue.filter((e: any) => e.__ref !== cacheID),
		},
	});
}

function addToPeopleOfCountryCache(cache: ApolloCache<any>, targetID: Country["id"], object: Pick<Person, "id">) {
	const objectRef = toCacheRef(cache, object);
	cache.modify({
		id: cache.identify({ id: targetID, __typename: graphTypeNames.Country }),
		fields: {
			people: (cachedValue) => [...cachedValue, objectRef],
		},
	});
}

function removeFromPeopleOfCountryCache(cache: ApolloCache<any>, targetID: Country["id"], object: Pick<Person, "id">) {
	const cacheID = cache.identify(object);
	cache.modify({
		id: cache.identify({ id: targetID, __typename: graphTypeNames.Country }),
		fields: {
			people: (cachedValue) => cachedValue.filter((e: any) => e.__ref !== cacheID),
		},
	});
}

function addToPersonOfPhoneNumberCache(
	cache: ApolloCache<any>,
	targetID: PhoneNumber["id"],
	object: Pick<Person, "id">,
) {
	const objectRef = toCacheRef(cache, object);
	cache.modify({
		id: cache.identify({ id: targetID, __typename: graphTypeNames.PhoneNumber }),
		fields: {
			person: () => objectRef,
			personID: () => object.id,
		},
	});
}

function removeFromPersonOfPhoneNumberCache(
	cache: ApolloCache<any>,
	targetID: PhoneNumber["id"],
	_object: Pick<Person, "id">,
) {
	cache.modify({
		id: cache.identify({ id: targetID, __typename: graphTypeNames.PhoneNumber }),
		fields: {
			person: () => null,
			personID: () => null,
		},
	});
}

function addToPersonOfStudentCache(cache: ApolloCache<any>, targetID: Student["id"], object: Pick<Person, "id">) {
	const objectRef = toCacheRef(cache, object);
	cache.modify({
		id: cache.identify({ id: targetID, __typename: graphTypeNames.Student }),
		fields: {
			person: () => objectRef,
			personID: () => object.id,
		},
	});
}

function removeFromPersonOfStudentCache(cache: ApolloCache<any>, targetID: Student["id"], _object: Pick<Person, "id">) {
	cache.modify({
		id: cache.identify({ id: targetID, __typename: graphTypeNames.Student }),
		fields: {
			person: () => null,
			personID: () => null,
		},
	});
}

function addToPersonOfUserCache(cache: ApolloCache<any>, targetID: User["id"], object: Pick<Person, "id">) {
	const objectRef = toCacheRef(cache, object);
	cache.modify({
		id: cache.identify({ id: targetID, __typename: graphTypeNames.User }),
		fields: {
			person: () => objectRef,
		},
	});
}

function removeFromPersonOfUserCache(cache: ApolloCache<any>, targetID: User["id"], _object: Pick<Person, "id">) {
	cache.modify({
		id: cache.identify({ id: targetID, __typename: graphTypeNames.User }),
		fields: {
			person: () => null,
		},
	});
}

function toCacheRef(cache: ApolloCache<any>, object: Pick<Person, "id">) {
	return (
		cache.writeFragment({
			fragment: gql`
				fragment PersonRef on Person {
					id
				}
			`,
			data: object,
		}) ?? null
	);
}
